import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, firstValueFrom, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ShellConfigService } from './shell-config.service';
import { TokenStorageService } from './token-storage.service';
import { AuthService } from './auth.service';

/** Options for a B2C login initiation. */
export interface B2cLoginOptions {
  /** B2C user-flow to run. Defaults to the sign-up/sign-in flow. */
  flow?: string;
  /** Theme hint forwarded to the hosted B2C page (default `'dark'`). */
  theme?: string;
  /** BCP-47 UI locale forwarded to the hosted B2C page (default `'en'`). */
  uiLocales?: string;
}

/** Result of completing a B2C login (the redirect-back / callback leg). */
export interface B2cLoginResult {
  success: boolean;
  /** Where to navigate after a successful sign-in (app-relative path). */
  returnUrl?: string;
  /** Error code on failure (e.g. `'access_denied'`, `'token_exchange_failed'`). */
  error?: string;
}

/** sessionStorage key holding the in-flight PKCE state across the B2C round-trip. */
const STATE_KEY = 'phx_b2c_login';
/** Default B2C user-flow — matches the main portal's `AuthFlow.SIGN_UP_SIGN_IN`. */
const DEFAULT_FLOW = 'SignUpSignIn';

interface PersistedLoginState {
  codeVerifier: string;
  flow: string;
  redirectUri: string;
  returnUrl: string;
}

interface LoginUrlResponse {
  loginUrl: string;
}
interface TokenExchangeResponse {
  accessToken?: string;
  expiresIn?: number;
}

/**
 * Frame-independent B2C login (ADO #1466). The counterpart to {@link DevAuthProvider}:
 * the DEV path signs in with mock credentials; THIS path runs the real Azure AD B2C
 * authorization-code + PKCE flow so a portal can go STRAIGHT to the hosted B2C page
 * instead of bouncing through the main Continity portal's `/login`.
 *
 * The whole flow is BACKEND-BROKERED — no MSAL, no B2C-policy knowledge in the FE:
 *   1. {@link initiateLogin} generates a PKCE pair, asks `GET /Auth/login-url` for the
 *      ready-made B2C authorize URL (`redirect_uri = <thisOrigin>/auth-callback`) and
 *      full-page-redirects to it. The PKCE verifier + return target are stashed in
 *      sessionStorage (survives the same-tab round-trip; cleared on tab close).
 *   2. B2C returns to the app's `/auth-callback` with a `code`; {@link completeLogin}
 *      exchanges it via `POST /Auth/token` (which ALSO lands the shared HttpOnly refresh
 *      cookie), stores the returned bearer and loads the user — after which the shell
 *      session machinery (currentUser$, refresh, guards) just works, exactly like dev-login.
 *
 * `redirect_uri = ${window.location.origin}/auth-callback` must be BOTH in the server
 * whitelist (`AzureB2C:AllowedRedirectUris`) AND a reply URL on the B2C app registration.
 */
@Injectable({ providedIn: 'root' })
export class B2cAuthProvider {
  private http = inject(HttpClient);
  private config = inject(ShellConfigService);
  private tokenStorage = inject(TokenStorageService);
  private auth = inject(AuthService);

  private get apiUrl(): string {
    return this.config.apiUrl;
  }
  private skipHeaders(): HttpHeaders {
    return new HttpHeaders().set('X-Skip-Interceptor', 'true');
  }

  /**
   * Begin the real B2C authorization-code + PKCE flow: full-page redirect to the hosted
   * B2C sign-in page. Rejects (without redirecting) if the login-url fetch fails, so the
   * caller can fall back; resolves just before the navigation (the browser then leaves).
   */
  async initiateLogin(returnUrl = '/board', opts: B2cLoginOptions = {}): Promise<void> {
    const { codeVerifier, codeChallenge } = await this.generatePkce();
    const redirectUri = `${window.location.origin}/auth-callback`;
    const flow = opts.flow ?? DEFAULT_FLOW;

    const state: PersistedLoginState = { codeVerifier, flow, redirectUri, returnUrl };
    sessionStorage.setItem(STATE_KEY, JSON.stringify(state));

    const query = new URLSearchParams({
      flow,
      returnUrl: '/auth-callback',
      codeChallenge,
      redirectUri,
      theme: opts.theme ?? 'dark',
      UiLocales: opts.uiLocales ?? 'en',
    });

    let res: LoginUrlResponse | undefined;
    try {
      res = await firstValueFrom(
        this.http.get<LoginUrlResponse>(`${this.apiUrl}/Auth/login-url?${query.toString()}`, {
          headers: this.skipHeaders(),
        })
      );
    } catch (err) {
      sessionStorage.removeItem(STATE_KEY);
      throw err;
    }
    if (!res?.loginUrl) {
      sessionStorage.removeItem(STATE_KEY);
      throw new Error('Failed to obtain B2C login URL');
    }
    window.location.href = res.loginUrl;
  }

  /**
   * Complete the callback leg: exchange `code` for tokens, land this host's cookie + bearer,
   * load the user. Returns a {@link B2cLoginResult} — never throws for expected auth errors
   * (user inactive, email not allowed, …); those surface as `success:false` with an `error`.
   */
  completeLogin(code: string): Observable<B2cLoginResult> {
    const raw = sessionStorage.getItem(STATE_KEY);
    if (!raw) {
      return of({ success: false, error: 'missing_login_state' });
    }
    let state: PersistedLoginState;
    try {
      state = JSON.parse(raw) as PersistedLoginState;
    } catch {
      sessionStorage.removeItem(STATE_KEY);
      return of({ success: false, error: 'missing_login_state' });
    }

    return this.http
      .post<TokenExchangeResponse>(
        `${this.apiUrl}/Auth/token`,
        {
          code,
          codeVerifier: state.codeVerifier,
          redirectUri: state.redirectUri,
          flow: state.flow,
        },
        { headers: this.skipHeaders(), withCredentials: true }
      )
      .pipe(
        switchMap((res) => {
          sessionStorage.removeItem(STATE_KEY);
          if (!res?.accessToken) {
            return of({ success: false, error: 'token_exchange_failed' } as B2cLoginResult);
          }
          this.tokenStorage.setToken(res.accessToken, res.expiresIn ?? 3600);
          this.auth.clearRolesCache();
          const returnUrl = state.returnUrl || '/board';
          // Load the canonical user so currentUser$ is populated before we navigate; a
          // failure here is non-fatal (guards re-fetch), so still report success.
          return this.auth.loadUser().pipe(
            map(() => ({ success: true, returnUrl } as B2cLoginResult)),
            catchError(() => of({ success: true, returnUrl } as B2cLoginResult))
          );
        }),
        catchError((err) => {
          sessionStorage.removeItem(STATE_KEY);
          return of({
            success: false,
            error:
              err?.error?.error ||
              err?.error?.error_description ||
              'token_exchange_failed',
          } as B2cLoginResult);
        })
      );
  }

  /** PKCE (S256): random verifier + its SHA-256 challenge, via Web Crypto. */
  private async generatePkce(): Promise<{ codeVerifier: string; codeChallenge: string }> {
    const verifierBytes = new Uint8Array(32);
    crypto.getRandomValues(verifierBytes);
    const codeVerifier = this.base64Url(verifierBytes);
    const digest = await crypto.subtle.digest(
      'SHA-256',
      new TextEncoder().encode(codeVerifier)
    );
    const codeChallenge = this.base64Url(new Uint8Array(digest));
    return { codeVerifier, codeChallenge };
  }

  private base64Url(bytes: Uint8Array): string {
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }
}
